import { Component } from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { AdminService } from 'src/app/services/admin-service';
import {
  AdminUserDetail,
  AdminUserSummary,
  UsersOverviewQuery,
} from 'src/app/models/adminUsers';

@Component({
  selector: 'pla-admin-users',
  templateUrl: './admin-users.component.html',
  styleUrls: ['./admin-users.component.scss'],
})
export class AdminUsersComponent {
  public users: AdminUserSummary[] = [];
  public totalCount: number = 0;
  public totalPages: number = 0;
  public loading: boolean = false;
  public errored: boolean = false;

  public query: UsersOverviewQuery = {
    search: '',
    page: 1,
    pageSize: 25,
    sortBy: 'createdDate',
    sortDir: 'Desc',
  };
  public pageSizeOptions: number[] = [10, 25, 50, 100];

  // Only these columns map to a server-side sort whitelist.
  private sortable: string[] = ['createdDate', 'lastLogin', 'email'];

  private searchSubject: Subject<string> = new Subject<string>();

  // Read-only detail drawer
  public drawerOpen: boolean = false;
  public detailLoading: boolean = false;
  public detailErrored: boolean = false;
  public selectedDetail: AdminUserDetail | null = null;

  constructor(private adminService: AdminService) {
    this.searchSubject
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((term) => {
        this.query.search = term;
        this.query.page = 1;
        this.loadUsers();
      });
    this.loadUsers();
  }

  public loadUsers(): void {
    this.loading = true;
    this.errored = false;
    this.adminService.getUsersOverview(this.query).subscribe({
      next: (resp) => {
        this.users = (resp && resp.users) || [];
        this.totalCount = (resp && resp.totalCount) || 0;
        this.totalPages = (resp && resp.totalPages) || 0;
        this.loading = false;
      },
      error: () => {
        this.users = [];
        this.totalCount = 0;
        this.totalPages = 0;
        this.loading = false;
        this.errored = true;
      },
    });
  }

  public onSearch(value: string): void {
    this.searchSubject.next(value || '');
  }

  public isSortable(col: string): boolean {
    return this.sortable.indexOf(col) !== -1;
  }

  public setSort(col: string): void {
    if (!this.isSortable(col)) return;
    if (this.query.sortBy === col) {
      this.query.sortDir = this.query.sortDir === 'Asc' ? 'Desc' : 'Asc';
    } else {
      this.query.sortBy = col;
      this.query.sortDir = 'Desc';
    }
    this.query.page = 1;
    this.loadUsers();
  }

  public sortIndicator(col: string): string {
    if (this.query.sortBy !== col) return '';
    return this.query.sortDir === 'Asc' ? ' ▲' : ' ▼';
  }

  public prevPage(): void {
    if (this.query.page > 1) {
      this.query.page--;
      this.loadUsers();
    }
  }

  public nextPage(): void {
    if (this.query.page < this.totalPages) {
      this.query.page++;
      this.loadUsers();
    }
  }

  public changePageSize(size: any): void {
    this.query.pageSize = +size;
    this.query.page = 1;
    this.loadUsers();
  }

  public openDetail(user: AdminUserSummary): void {
    this.drawerOpen = true;
    this.detailLoading = true;
    this.detailErrored = false;
    this.selectedDetail = null;
    this.adminService.getUserDetail(user.id).subscribe({
      next: (detail) => {
        this.selectedDetail = detail;
        this.detailLoading = false;
      },
      error: () => {
        this.detailLoading = false;
        this.detailErrored = true;
      },
    });
  }

  public closeDetail(): void {
    this.drawerOpen = false;
    this.selectedDetail = null;
  }
}
